/* ========================
 * NotesManager.jsx - Manages note and todo data, state, and Firestore interactions
 * Implements on-demand loading/unloading of items.
 * ======================== */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { db } from '@/firebase/config';
import { collection, doc, setDoc, deleteDoc, query, onSnapshot } from 'firebase/firestore'; // Removed 'where' as it's not used in this specific file's direct queries
import { authManager } from '@/managers/AuthManager';
import { CryptoUtils } from '@/utils/cryptoUtils';
import debounce from 'lodash/debounce';

const DEBOUNCE_MS = 1000; // 1 second debounce

/**
 * @typedef {Object} Note
 * @property {string} id
 * @property {string} content
 * @property {string[]} tags
 * @property {'note' | 'todo'} type
 * @property {string} createdAt - ISO date string
 * @property {string} updatedAt - ISO date string
 * @property {number} order
 * @property {boolean} completed
 * @property {'low' | 'normal' | 'high'} priority
 * @property {string} [dueDate] - ISO date string (optional)
 */

// Helper Functions
/* ========================
 * safeDecrypt - Safely decrypts data, returning original on error
 * ======================== */
const safeDecrypt = async (data, userId) => {
    if (!data) return '';
    try {
        const decrypted = await CryptoUtils.decrypt(data, userId);
        return String(decrypted);
    } catch (error) {
        console.warn('Decryption failed:', error);
        return String(data);
    }
};

/* ========================
 * safeJSONParse - Safely parses JSON, returning a fallback on error
 * ======================== */
const safeJSONParse = (str, fallback = []) => {
    if (!str) return fallback;
    if (Array.isArray(str)) return str;
    try {
        const parsed = JSON.parse(str);
        return Array.isArray(parsed) ? parsed : fallback;
    } catch (error) {
        // console.warn('JSON parse failed, returning fallback for string:', str);
        return fallback;
    }
};

const NotesContext = createContext();

/* ========================
 * useNotes - Hook to access NotesContext
 * ======================== */
export const useNotes = () => {
    const context = useContext(NotesContext);
    if (!context) throw new Error('useNotes must be used within NotesManager');
    return context;
};

/* ========================
 * NotesManager - Provider component for notes and todos management
 * ======================== */
const NotesManager = ({ children }) => {
    const { user } = authManager.getState();
    const [items, setItems] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [view, setView] = useState('all'); // 'all', 'notes', 'todos'
    const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'updated'
    const [isLoading, setIsLoading] = useState(false); // Changed initial to false, will be true during load
    const [error, setError] = useState(null);
    
    const pendingUpdates = useRef(new Map());
    const unsubscribeRef = useRef(null);

    /* ========================
     * debouncedSave - Debounces item save operations to Firestore
     * ======================== */
    const debouncedSave = useCallback(
        debounce(async (itemId, updates) => {
            if (!user?.uid) return;

            try {
                const notesRef = collection(db, `users/${user.uid}/notes`);
                const itemRef = doc(notesRef, itemId);
                
                const encryptedUpdates = {};

                if (updates.content !== undefined) {
                    encryptedUpdates.content = await CryptoUtils.encrypt(
                        String(updates.content || ''),
                        user.uid
                    );
                }
                if (updates.tags !== undefined) {
                    encryptedUpdates.tags = await CryptoUtils.encrypt(
                        JSON.stringify(Array.isArray(updates.tags) ? updates.tags : []),
                        user.uid
                    );
                }
                // Encrypt dueDate if present and not null
                if (updates.dueDate !== undefined) {
                    encryptedUpdates.dueDate = updates.dueDate 
                        ? await CryptoUtils.encrypt(updates.dueDate, user.uid)
                        : null; // Store null if dueDate is explicitly set to null/undefined
                }


                const finalUpdates = {
                    ...encryptedUpdates,
                    updatedAt: new Date().toISOString(),
                    // Non-encrypted fields
                    ...(updates.type && { type: updates.type }),
                    ...(updates.order !== undefined && { order: updates.order }),
                    ...(updates.completed !== undefined && { completed: updates.completed }),
                    ...(updates.priority && { priority: updates.priority })
                };

                await setDoc(itemRef, finalUpdates, { merge: true });
                pendingUpdates.current.delete(itemId);
            } catch (error) {
                console.error('Failed to save item:', error);
                setError(`Failed to save item ${itemId}`);
                // Do not throw, let the UI handle the error state
            }
        }, DEBOUNCE_MS),
        [user?.uid]
    );

    /* ========================
     * loadNotesAndTodos - Loads items from Firestore and sets up a listener
     * ======================== */
    const loadNotesAndTodos = useCallback(async () => {
        if (!user?.uid || unsubscribeRef.current) { // No user or already listening
            if (unsubscribeRef.current && items.length > 0) setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        // console.log('Loading notes and todos...');

        const notesRef = collection(db, `users/${user.uid}/notes`);
        const userItemsQuery = query(notesRef); // Add orderBy if needed for initial fetch before client-side sort

        unsubscribeRef.current = onSnapshot(
            userItemsQuery,
            // { includeMetadataChanges: true }, // Removed for simplicity, process all snapshots
            async (snapshot) => {
                try {
                    // Process all snapshot updates to ensure UI consistency
                    const decryptedItems = await Promise.all(
                        snapshot.docs.map(async (docSnapshot) => {
                            const item = docSnapshot.data();
                            const decryptedDueDate = item.dueDate 
                                ? await safeDecrypt(item.dueDate, user.uid) 
                                : undefined;

                            return {
                                id: docSnapshot.id,
                                content: await safeDecrypt(item.content, user.uid),
                                tags: safeJSONParse(await safeDecrypt(item.tags, user.uid), []),
                                type: item.type || 'note', // Default to 'note' if undefined
                                createdAt: item.createdAt || new Date().toISOString(),
                                updatedAt: item.updatedAt || new Date().toISOString(),
                                order: item.order !== undefined ? item.order : Date.parse(item.createdAt || 0),
                                completed: item.completed || false,
                                priority: item.priority || 'normal',
                                ...(decryptedDueDate && { dueDate: decryptedDueDate })
                            };
                        })
                    );
                    setItems(decryptedItems);
                    setError(null);
                } catch (err) {
                    console.error('Error processing notes/todos snapshot:', err);
                    setError('Failed to load items');
                    setItems([]);
                } finally {
                    setIsLoading(false);
                }
            },
            (err) => {
                console.error('Notes/Todos sync error:', err);
                setError('Failed to sync items');
                setItems([]);
                setIsLoading(false);
            }
        );
    }, [user?.uid, items.length]); // items.length added to re-evaluate setIsLoading if called when items exist

    /* ========================
     * unloadNotesAndTodos - Clears local items and unsubscribes from Firestore
     * ======================== */
    const unloadNotesAndTodos = useCallback(() => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
        setItems([]);
        setIsLoading(false);
        setError(null);
        // console.log('Notes and todos unloaded');
    }, []);
    
    // Cleanup listener and debounced saves on unmount or user change
    useEffect(() => {
        return () => {
            debouncedSave.cancel();
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [debouncedSave, user?.uid]); // Added user.uid

    /* ========================
     * addNote - Adds a new note item to Firestore
     * ======================== */
    const addNote = useCallback(async (content, tags = [], options = {}) => {
        if (!user?.uid) throw new Error('User must be authenticated to add a note.');
        
        try {
            setIsLoading(true);
            const currentTimestamp = new Date().toISOString();
            // Use timestamp for default order if items are not loaded, or fallback to length-based.
            const defaultOrder = items.length > 0 
                ? Math.max(...items.map(item => item.order), 0) + 1 
                : Date.parse(currentTimestamp);

            const noteId = `note_${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const notesRef = collection(db, `users/${user.uid}/notes`);
            const noteRef = doc(notesRef, noteId);

            const encryptedContent = await CryptoUtils.encrypt(String(content || ''), user.uid);
            const encryptedTags = await CryptoUtils.encrypt(JSON.stringify(Array.isArray(tags) ? tags : []), user.uid);

            const noteData = {
                content: encryptedContent,
                tags: encryptedTags,
                type: 'note',
                createdAt: currentTimestamp,
                updatedAt: currentTimestamp,
                order: options.order !== undefined ? options.order : defaultOrder,
                priority: options.priority || 'normal',
                completed: false // Notes are not completable in this structure
            };

            await setDoc(noteRef, noteData);
            setIsLoading(false);
            return noteId;
        } catch (error) {
            console.error('Failed to add note:', error);
            setError('Failed to add note');
            setIsLoading(false);
            throw error; // Re-throw for the caller to handle
        }
    }, [items, user?.uid]);

    /* ========================
     * addTodo - Adds a new todo item to Firestore
     * ======================== */
    const addTodo = useCallback(async (content, dueDate = null, tags = [], options = {}) => {
        if (!user?.uid) throw new Error('User must be authenticated to add a todo.');
        
        try {
            setIsLoading(true);
            const currentTimestamp = new Date().toISOString();
            const defaultOrder = items.length > 0 
                ? Math.max(...items.map(item => item.order), 0) + 1 
                : Date.parse(currentTimestamp);

            const todoId = `todo_${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const notesRef = collection(db, `users/${user.uid}/notes`); // Still uses 'notes' collection
            const todoRef = doc(notesRef, todoId);

            const encryptedContent = await CryptoUtils.encrypt(String(content || ''), user.uid);
            const encryptedTags = await CryptoUtils.encrypt(JSON.stringify(Array.isArray(tags) ? tags : []), user.uid);
            const encryptedDueDate = dueDate 
                ? await CryptoUtils.encrypt(new Date(dueDate).toISOString(), user.uid) // Ensure ISO string
                : null;

            const todoData = {
                content: encryptedContent,
                tags: encryptedTags,
                dueDate: encryptedDueDate,
                type: 'todo',
                completed: options.completed || false,
                createdAt: currentTimestamp,
                updatedAt: currentTimestamp,
                order: options.order !== undefined ? options.order : defaultOrder,
                priority: options.priority || 'normal'
            };

            await setDoc(todoRef, todoData);
            setIsLoading(false);
            return todoId;
        } catch (error) {
            console.error('Failed to add todo:', error);
            setError('Failed to add todo');
            setIsLoading(false);
            throw error;
        }
    }, [items, user?.uid]);

    /* ========================
     * updateNote - Updates an existing item with optimistic UI and debounced save
     * Renamed to updateItem for clarity as it handles notes and todos.
     * ======================== */
    const updateItem = useCallback(async (itemId, updates) => {
        if (!user?.uid) throw new Error('User must be authenticated to update an item.');

        if (items.length > 0) { // Optimistic update only if items are loaded
            setItems(prev => prev.map(item => 
                item.id === itemId 
                    ? { ...item, ...updates, updatedAt: new Date().toISOString() }
                    : item
            ));
        }
        
        const currentPending = pendingUpdates.current.get(itemId) || {};
        pendingUpdates.current.set(itemId, { ...currentPending, ...updates });
        debouncedSave(itemId, pendingUpdates.current.get(itemId));
    }, [user?.uid, debouncedSave, items.length]);

    /* ========================
     * deleteItem - Deletes an item from Firestore
     * Renamed from deleteNote for clarity.
     * ======================== */
    const deleteItem = useCallback(async (itemId) => {
        if (!user?.uid) throw new Error('User must be authenticated to delete an item.');
        
        try {
            setIsLoading(true);
            // debouncedSave.cancel(); // Avoid broad cancellation.
            pendingUpdates.current.delete(itemId); // Clear pending for this specific item

            const notesRef = collection(db, `users/${user.uid}/notes`);
            const itemRef = doc(notesRef, itemId);
            await deleteDoc(itemRef);
            // Listener will update local state if active.
            setIsLoading(false);
        } catch (error) {
            console.error('Failed to delete item:', error);
            setError('Failed to delete item');
            setIsLoading(false);
            throw error;
        }
    }, [user?.uid]);

    /* ========================
     * toggleTodo - Toggles the completed state of a todo item
     * ======================== */
    const toggleTodo = useCallback(async (todoId) => {
        const todo = items.find(item => item.id === todoId && item.type === 'todo');
        if (!todo) return; // Or throw error

        // Use updateItem for consistency
        await updateItem(todoId, { completed: !todo.completed });
    }, [items, updateItem]);

    /* ========================
     * getFilteredItems - Filters and sorts items based on current view, search, and tags
     * ======================== */
    const getFilteredItems = useCallback(() => {
        let filtered = [...items];

        if (view === 'notes') {
            filtered = filtered.filter(item => item.type === 'note');
        } else if (view === 'todos') {
            filtered = filtered.filter(item => item.type === 'todo');
        }

        if (searchQuery) {
            const queryLower = searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                item.content?.toLowerCase().includes(queryLower) ||
                (Array.isArray(item.tags) && item.tags.some(tag => tag.toLowerCase().includes(queryLower)))
            );
        }

        if (selectedTags.length > 0) {
            filtered = filtered.filter(item =>
                Array.isArray(item.tags) && selectedTags.every(tag => item.tags.includes(tag))
            );
        }
        
        // Sort by order first, then by the selected sort criteria
        // The primary sort should always be 'order' for draggable lists.
        // Secondary sort can be applied if 'order' is not discriminative enough or not used.
        // For now, let's assume 'order' is the primary sort for DND.
        // The 'sortBy' will apply to how items are initially displayed or if 'order' is equal.
        
        filtered.sort((a, b) => {
            // Primary sort by 'order'
            const orderA = a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
            const orderB = b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) {
                return orderA - orderB;
            }

            // Secondary sort by selected criteria if order is the same
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            const dateUpdatedA = new Date(a.updatedAt);
            const dateUpdatedB = new Date(b.updatedAt);

            switch (sortBy) {
                case 'oldest':
                    return dateA - dateB;
                case 'updated':
                    return dateUpdatedB - dateUpdatedA;
                case 'newest':
                default:
                    return dateB - dateA;
            }
        });


        return filtered;
    }, [items, view, searchQuery, selectedTags, sortBy]);

    /* ========================
     * getAllTags - Gets all unique tags from the currently loaded items
     * ======================== */
    const getAllTags = useCallback(() => {
        const tagSet = new Set();
        items.forEach(item => {
            if (Array.isArray(item.tags)) {
                item.tags.forEach(tag => {
                    if (tag && typeof tag === 'string') {
                        tagSet.add(tag);
                    }
                });
            }
        });
        return Array.from(tagSet).sort();
    }, [items]);

    /* ========================
     * reorderItems - Updates the order of items in Firestore
     * ======================== */
    const reorderItems = useCallback(async (newOrderedItems) => {
        if (!user?.uid || !Array.isArray(newOrderedItems)) throw new Error('Invalid arguments for reorderItems');

        // Optimistically update local state for smoother UX
        if (items.length > 0) {
            const newItemsMap = new Map(newOrderedItems.map((item, index) => [item.id, { ...item, order: index }]));
            setItems(prevItems =>
                prevItems
                    .map(pi => newItemsMap.has(pi.id) ? newItemsMap.get(pi.id) : pi)
                    .sort((a, b) => (a.order || 0) - (b.order || 0)) // Ensure sorted by new order
            );
        }
        
        try {
            setIsLoading(true); // Indicate activity for reordering
            const updates = newOrderedItems.map((item, index) => 
                updateItem(item.id, { order: index }) // updateItem is debounced
            );
            await Promise.all(updates);
            // Firestore listener will eventually update the local state correctly if optimistic fails or for other clients.
            setIsLoading(false);
        } catch (error) {
            console.error('Failed to reorder items:', error);
            setError('Failed to reorder items');
            setIsLoading(false);
            // Consider a rollback or rely on Firestore sync to correct.
            throw error;
        }
    }, [user?.uid, updateItem, items.length]);

    const value = {
        items: getFilteredItems(), // Provides currently filtered items
        rawItems: items, // Provide raw items if needed by any component directly
        isLoading,
        error,
        addNote,
        addTodo,
        updateItem, // Renamed from updateNote
        deleteItem, // Renamed from deleteNote
        toggleTodo,
        getAllTags,
        // getFilteredItems, // This is used internally to provide `items`
        reorderItems,
        searchQuery,
        setSearchQuery,
        selectedTags,
        setSelectedTags,
        view,
        setView,
        sortBy,
        setSortBy,
        loadNotesAndTodos,    // Expose load function
        unloadNotesAndTodos,  // Expose unload function
        isNotesLoaded: !!unsubscribeRef.current && items.length > 0, // Status flag
    };

    return (
        <NotesContext.Provider value={value}>
            {children}
        </NotesContext.Provider>
    );
};

export default NotesManager;